import { useEffect, useMemo, useState } from 'react';
import { PersonRow } from './PersonRow';
import { FiChevronDown, FiSlash } from 'react-icons/fi';
import { GrPowerReset } from 'react-icons/gr';
import { FaSortAmountDownAlt } from 'react-icons/fa';
import { BiMessageDetail } from 'react-icons/bi';
import NoDataPlaceholder from '../../../assets/images/noIcon.svg';
import SelectionBanner from './ActionBanner';
import AiPrompt from './AiPrompt';
import { Skeleton } from '@chakra-ui/react';
import { HiOutlineDocumentText } from 'react-icons/hi';
export const CandidatesList = ({
  peopleData,
  jobs,
  setSelectedRole,
  setTop,
  selectedRole,
  isLoading,
  resumeStats,
}) => {
  const [people, setPeople] = useState(peopleData);
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [filters, setFilters] = useState({
    role: '',
    match: '',
    location: '',
    status: '',
  });
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);

  useEffect(() => setPeople(peopleData), [peopleData]);

  const updateFilter = (key: string, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));
  const resetFilters = () => {
    setFilters({ role: '', match: '', location: '', status: '' });
    setSelectedRole('');
    setTop(100);
  };
  const toggleCheck = (id: string) =>
    setPeople((prev) =>
      prev.map((p) => (p._id === id ? { ...p, checked: !p.checked } : p))
    );

  const updateStatusForChecked = (status: string) => {
    setPeople((prev) =>
      prev.map((p) => (p.checked ? { ...p, status, checked: false } : p))
    );
    setFilters((prev) => ({ ...prev, status }));
  };
  const filteredPeople = useMemo(() => {
    return people?.filter((p) => {
      const matchPercent = filters.match
        ? parseInt(filters.match.replace('%', ''))
        : null;

      return (
        (!filters.role || p.primaryJob?.name === filters.role) &&
        (!matchPercent || p.primaryJob?.match >= matchPercent) &&
        (!filters.location || p.location === filters.location) &&
        (!filters.status || p.status === filters.status)
      );
    });
  }, [people, filters]);
  const filterConfig = [
    {
      key: 'role',
      label: 'Select Role',
      options: jobs?.map((r) => r.jobTitle) || [],
    },
    {
      key: 'match',
      label: 'Min match %',
      options: ['50%', '60%', '70%', '80%'],
    },
    {
      key: 'location',
      label: 'Location',
      options: Array.from(new Set(jobs?.map((r) => r.location))) || [],
    },
    {
      key: 'status',
      label: 'Status',
      options: ['New', 'Shortlist', 'Rejected'],
    },
  ];
  const ActionButton = ({ onClick, icon: Icon, label, className = '' }) => (
    <button
      onClick={onClick}
      className={`flex-1 min-w-15 flex items-center justify-center gap-2 font-semibold text-[13px] py-2 rounded-[10px] ${className}`}
    >
      {Icon && <Icon size={18} />}
      {label}
    </button>
  );
  const selectedCandidates = people.filter((p) => p.checked);
  return (
    <div className="flex-1 flex flex-col gap-4 min-w-0">
      {' '}
      <h1 className="text-2xl text-[#6E3EF5] font-semibold">Role Matching</h1>
      <div className="flex gap-2.5 flex-wrap">
        {filterConfig.map(({ key, label, options }) => (
          <div key={key} className="flex-1 relative">
            <select
              value={filters[key]}
              onChange={(e) => updateFilter(key, e.target.value)}
               className="w-full  border-[#D4D4D4] border-[0.5px] rounded-lg px-5 py-3 pr-8 text-sm text-gray-700 appearance-none cursor-pointer focus:outline-none"
            >
              <option value="">{label}</option>
              {options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
            <FiChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" />
          </div>
        ))}
      </div>
      <div className="w-full grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        <ActionButton
          onClick={resetFilters}
          icon={GrPowerReset}
          label="Reset"
          className="bg-[#F9F6FF] text-[#707070]"
        />

        {/* Shortlist dropdown */}
        <div className="relative h-[44px]">
          <button
            onClick={() => setStatusMenuOpen((prev) => !prev)}
            className="h-full w-full flex items-center justify-center gap-2 bg-[#F9F6FF] text-[#707070] font-semibold text-[13px] rounded-[10px]"
          >
            <FaSortAmountDownAlt size={16} />
            Shortlist
          </button>

          {statusMenuOpen && (
            <div className="absolute top-full left-0 mt-1 bg-white  border-[#D4D4D4] border-[0.5px] rounded shadow-lg z-50 w-full">
              {['New', 'Shortlist', 'Rejected'].map((status) => (
                <button
                  key={status}
                  onClick={() => {
                    updateStatusForChecked(status);
                    setStatusMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100"
                >
                  {status}
                </button>
              ))}
            </div>
          )}
        </div>
        <ActionButton
          onClick={() => updateStatusForChecked('Rejected')}
          icon={FiSlash}
          label="Reject"
          className="bg-[#FFDFDF] text-[#707070]"
        />

        <button className="h-[44px] flex items-center justify-center gap-1 bg-gradient-to-r from-[#0F9B8E] to-[#6DD5C4] text-white font-bold text-[13px] px-3 rounded-[10px] text-center">
          <HiOutlineDocumentText size={18} />
          <p className="flex flex-col text-left">
            <span className="text-[9px]">
              {resumeStats?.pending ?? 0} Pending
            </span>
            <span className="text-[9px]">
              {resumeStats?.processed ?? 0} Processed
            </span>
          </p>
        </button>

      </div>
      <div className="flex flex-col  border-[#D4D4D4] border-[0.5px] rounded-xl">
        <div className="px-4 pt-6">
          <h1 className="text-lg font-semibold">Candidates</h1>
          <hr className="mt-2 border-t-[0.5px] border-[#D4D4D4]" />
        </div>
        <div className="flex flex-col gap-3 max-h-[525px] overflow-y-auto px-4 py-4">
          {isLoading ? (
            <div className="flex flex-col gap-3 py-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton h="40px" w="40px" borderRadius="full" />
                  <div className="flex flex-col gap-2 flex-1">
                    <Skeleton h="12px" w="60%" />
                    <Skeleton h="10px" w="40%" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPeople.length > 0 ? (
            filteredPeople.map((p) => (
              <PersonRow
                key={p.id}
                person={p}
                onToggle={toggleCheck}
                disabled={!selectedRole && !filters.role}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-20 h-60">
              <img
                src={NoDataPlaceholder}
                alt="No data"
                className="h-52 w-full opacity-60"
              />
            </div>
          )}
        </div>
      </div>
      {selectedCandidates.length > 0 && (
        <div className="absolute bottom-0 left-0 w-full z-50">
          <SelectionBanner selectedCandidates={selectedCandidates} />
        </div>
      )}
      {isPromptOpen && (
        <AiPrompt open={isPromptOpen} onClose={() => setIsPromptOpen(false)} />
      )}
    </div>
  );
};
